import { Button, ButtonIcon, Modal, Typography, useMobile } from '@sonnen/web-ui';
import { useAppContext } from '../../AppContext/AppContext';
import { useTranslation } from '../../i18n/i18n';
import { ContractCreationBlockedReason } from '../../types';
import { FullContentMessage } from '../FullContentMessage/FullContentMessage';
import styles from './ContractCreationBlocked.module.css';
import QR from './QR';
import { SonnenAppLogo } from './sonnenAppLogo';
import StoreBadge from './StoreBadge';

export const storeVariant = ['appStore', 'playStore'] as const;
export type StoreVariant = (typeof storeVariant)[number];

const QRCodeModal = ({ variant, onClick }: { variant: StoreVariant; onClick: () => void }) => {
  const { t } = useTranslation();

  return (
    <Modal dataTestId="store-QR-code-modal">
      <div className={styles.qrCodeModalContent}>
        <ButtonIcon
          icon="Close"
          onClick={onClick}
          dataTestId="close-modal-btn"
          ariaLabel="Close modal"
        />
        <SonnenAppLogo />
        <span className={styles.text}>
          <Typography.H4>{t('contractCreationBlocked.qrCodeModal')}</Typography.H4>
        </span>
        <QR store={variant} />
      </div>
    </Modal>
  );
};

export const ContractNotAvailable = () => {
  const { t } = useTranslation();

  const goToWebsite = () => window.open('https://sonnen.de', '_blank');

  return (
    <FullContentMessage
      dataTestId="contract-not-available"
      title={t('contractCreationBlocked.notAvailable.title')}
      text={t('contractCreationBlocked.notAvailable.text')}
    >
      <Button
        size="medium"
        variant="secondary"
        onClick={goToWebsite}
        dataTestId="go-to-website-btn"
        label={t('contractCreationBlocked.notAvailable.toWebsiteBtn')}
      />
    </FullContentMessage>
  );
};

const ConcludedSonnenHomeContract = () => {
  const { setCurrentModal } = useAppContext();
  const { t } = useTranslation();

  const onClick = (store: StoreVariant) =>
    setCurrentModal(<QRCodeModal variant={store} onClick={() => setCurrentModal(null)} />);

  return (
    <div
      className={styles.concludedSonnenHomeContract}
      data-testid="concluded-sonnen-home-contract"
    >
      <Typography.Display>{t('contractCreationBlocked.hasContract.title')}</Typography.Display>
      <div>
        <Typography.Body1>{t('contractCreationBlocked.hasContract.text')}</Typography.Body1>
      </div>

      <div className={styles.storeButtons}>
        {storeVariant.map((store) => (
          <button key={store} onClick={() => onClick(store)}>
            <StoreBadge store={store} />
          </button>
        ))}
      </div>
    </div>
  );
};

const ConcludedSonnenHomeContractMobile = () => {
  const { t } = useTranslation();

  const appleStoreLink = 'https://apps.apple.com/de/app/sonnen/id6444147829';
  const playStoreLink = 'https://play.google.com/store/apps/details?id=com.sonnen_mobile';

  const sonnenAppStoreLink = /iPhone/.test(navigator.userAgent) ? appleStoreLink : playStoreLink;

  return (
    <div className={styles.concludedSonnenHomeContractForMobile}>
      <Typography.H1>{t(`contractCreationBlocked.hasContract.title`)}</Typography.H1>

      <div className={styles.mobileText}>
        <Typography.Body1>{t(`contractCreationBlocked.hasContract.text`)}</Typography.Body1>
      </div>
      <Button
        size="medium"
        onClick={() => window.open(sonnenAppStoreLink)}
        dataTestId="app-store-btn"
        label="Open sonnenApp"
        iconRight="ArrowRight"
        expanded
      />
    </div>
  );
};

const ContractCreationBlocked = ({ reason }: { reason: ContractCreationBlockedReason }) => {
  const mobile = useMobile();

  if (reason === 'notAvailable') return <ContractNotAvailable />;

  if (reason === 'sonnenHome') {
    return mobile ? <ConcludedSonnenHomeContractMobile /> : <ConcludedSonnenHomeContract />;
  }
};

export default ContractCreationBlocked;
